import { EmployeeRecord } from '../../utils/employee-indexeddb';
import { getUsageRateClass, formatNumber } from './format-helpers';
import { generateHeadquarterStats } from './stats';

/**
 * Generate HTML report for headquarter statistics
 */
export function generateHeadquarterHtmlReport(
  employees: EmployeeRecord[],
  reportTitle: string = 'API利用状況報告',
  reportPeriod: string = '過去30日間の利用状況'
): string {
  const stats = generateHeadquarterStats(employees);

  // Calculate summary statistics
  const totalHeadquarters = stats.length;
  const totalRequests = stats.reduce((sum, s) => sum + s.totalRequests, 0);
  const totalUsers = stats.reduce((sum, s) => sum + s.totalUsers, 0);
  const averageRequests = totalUsers > 0 ? totalRequests / totalUsers : 0;
  const totalActive = stats.reduce((sum, s) => sum + s.activeUsers, 0);
  const averageUsageRate = totalUsers > 0 ? (totalActive / totalUsers) * 100 : 0;

  // Generate table rows
  const tableRows = stats.map(stat => `
                    <tr>
                        <td class="dept-name">${stat.headquarter}</td>
                        <td class="usage-rate ${getUsageRateClass(stat.usageRate)}">${stat.usageRate.toFixed(1)}%</td>
                        <td class="number">${formatNumber(stat.activeUsers)}</td>
                        <td class="number">${formatNumber(stat.inactiveUsers)}</td>
                        <td class="number">${formatNumber(stat.totalUsers)}</td>
                        <td class="number">${formatNumber(stat.totalRequests)}</td>
                        <td class="number">${stat.totalUsers > 0 ? stat.averageRequests.toFixed(1) : '0.0'}</td>
                    </tr>`).join('\n');

  const currentDate = new Date().toLocaleDateString('ja-JP', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit'
  }).replace(/\//g, '月').replace(/月(\d+)$/, '月$1日') + '時点';

  // Generate HTML
  return `<!DOCTYPE html>
<html lang="ja">
<head>
    <meta charset="UTF-8">
    <meta name="viewport" content="width=device-width, initial-scale=1.0">
    <title>${reportTitle} - 本部別集計</title>
    <style>
        * {
            margin: 0;
            padding: 0;
            box-sizing: border-box;
        }

        body {
            font-family: 'Segoe UI', 'Yu Gothic', 'Meiryo', sans-serif;
            background: #0f172a;
            padding: 20px;
            min-height: 100vh;
        }

        .container {
            max-width: 1400px;
            margin: 0 auto;
            background: #1e293b;
            border-radius: 12px;
            box-shadow: 0 10px 40px rgba(0,0,0,0.5);
            overflow: hidden;
        }

        .header {
            background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
            color: white;
            padding: 30px;
            text-align: center;
        }

        .header h1 {
            font-size: 28px;
            margin-bottom: 10px;
        }

        .header p {
            font-size: 14px;
            opacity: 0.9;
        }

        .summary {
            display: grid;
            grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
            gap: 20px;
            padding: 30px;
            background: #0f172a;
        }

        .summary-card {
            background: #334155;
            padding: 20px;
            border-radius: 8px;
            box-shadow: 0 2px 8px rgba(0,0,0,0.3);
            text-align: center;
        }

        .summary-card h3 {
            font-size: 14px;
            color: #94a3b8;
            margin-bottom: 10px;
            text-transform: uppercase;
            letter-spacing: 0.5px;
        }

        .summary-card p {
            font-size: 32px;
            font-weight: bold;
            color: #818cf8;
        }

        .table-container {
            padding: 30px;
            overflow-x: auto;
            background: #1e293b;
        }

        table {
            width: 100%;
            border-collapse: collapse;
            font-size: 14px;
        }

        thead {
            background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
            color: white;
        }

        th {
            padding: 15px 10px;
            text-align: left;
            font-weight: 600;
            cursor: pointer;
            user-select: none;
            position: relative;
        }

        th:hover {
            background: rgba(255,255,255,0.1);
        }

        th::after {
            content: ' ↕';
            opacity: 0.5;
            font-size: 12px;
        }

        td {
            padding: 12px 10px;
            border-bottom: 1px solid #334155;
            color: #e2e8f0;
        }

        tbody tr {
            transition: background 0.2s;
            background: #1e293b;
        }

        tbody tr:hover {
            background: #334155;
        }

        .dept-name {
            font-weight: 600;
            color: #f1f5f9;
        }

        .usage-rate {
            font-weight: 600;
        }

        .rate-high {
            color: #28a745;
        }

        .rate-medium {
            color: #ffc107;
        }

        .rate-low {
            color: #dc3545;
        }

        .number {
            text-align: right;
            font-family: 'Courier New', monospace;
            color: #cbd5e1;
        }

        .cost {
            text-align: right;
            font-weight: 600;
            color: #818cf8;
        }

        .footer {
            padding: 20px 30px;
            background: #0f172a;
            text-align: center;
            font-size: 12px;
            color: #94a3b8;
        }

        @media (max-width: 768px) {
            .summary {
                grid-template-columns: 1fr;
            }

            table {
                font-size: 12px;
            }

            th, td {
                padding: 8px 5px;
            }
        }

        @media print {
            body {
                background: #0f172a;
                padding: 0;
            }

            .container {
                box-shadow: none;
            }
        }
    </style>
</head>
<body>
    <div class="container">
        <div class="header">
            <h1>${reportTitle}</h1>
            <p>${reportPeriod}（${currentDate}） - 本部別集計</p>
        </div>

        <div class="summary">
            <div class="summary-card">
                <h3>総本部数</h3>
                <p>${formatNumber(totalHeadquarters)}</p>
            </div>
            <div class="summary-card">
                <h3>総リクエスト数</h3>
                <p>${formatNumber(totalRequests)}</p>
            </div>
            <div class="summary-card">
                <h3>平均リクエスト数</h3>
                <p>${averageRequests.toFixed(1)}</p>
            </div>
            <div class="summary-card">
                <h3>平均利用率</h3>
                <p>${averageUsageRate.toFixed(1)}%</p>
            </div>
        </div>

        <div class="table-container">
            <table id="usageTable">
                <thead>
                    <tr>
                        <th onclick="sortTable(0)">本部名</th>
                        <th onclick="sortTable(1)">利用率</th>
                        <th onclick="sortTable(2)">利用者数</th>
                        <th onclick="sortTable(3)">未利用者数</th>
                        <th onclick="sortTable(4)">総人数</th>
                        <th onclick="sortTable(5)">合計リクエスト</th>
                        <th onclick="sortTable(6)">平均リクエスト</th>
                    </tr>
                </thead>
                <tbody>
${tableRows}
                </tbody>
            </table>
        </div>

        <div class="footer">
            <p>※ OpenAI APIのみの集計となります。他のAPIを使用している場合は現時点では利用状況に反映されません。</p>
            <p>※ OpenAI側で仕様変更が頻繁に発生しており正確な集計ではないです。概算程度と思っていただければ幸いです。</p>
            <p>Generated by Usage Dashboard - ${new Date().toLocaleString('ja-JP')}</p>
        </div>
    </div>

    <script>
        let sortDirection = {};

        function sortTable(columnIndex) {
            const table = document.getElementById('usageTable');
            const tbody = table.querySelector('tbody');
            const rows = Array.from(tbody.querySelectorAll('tr'));

            // Toggle sort direction
            sortDirection[columnIndex] = !sortDirection[columnIndex];
            const ascending = sortDirection[columnIndex];

            rows.sort((a, b) => {
                let aValue = a.cells[columnIndex].textContent.trim();
                let bValue = b.cells[columnIndex].textContent.trim();

                // Handle percentage values
                if (aValue.includes('%')) {
                    aValue = parseFloat(aValue.replace('%', ''));
                    bValue = parseFloat(bValue.replace('%', ''));
                }
                // Handle currency values
                else if (aValue.includes('$') || aValue.includes('¥')) {
                    aValue = parseFloat(aValue.replace(/[$¥]/g, '').replace(/,/g, ''));
                    bValue = parseFloat(bValue.replace(/[$¥]/g, '').replace(/,/g, ''));
                }
                // Handle numeric values with comma separators
                else if (!isNaN(parseFloat(aValue.replace(/,/g, '')))) {
                    aValue = parseFloat(aValue.replace(/,/g, ''));
                    bValue = parseFloat(bValue.replace(/,/g, ''));
                }

                if (aValue < bValue) return ascending ? -1 : 1;
                if (aValue > bValue) return ascending ? 1 : -1;
                return 0;
            });

            // Re-append sorted rows
            rows.forEach(row => tbody.appendChild(row));
        }
    </script>
</body>
</html>`;
}
